/* ──────────────────────────────────────────────────────────────
   Stage 3 (L3 · CNC review) — frontend journey rules.
   Mirrors CRM_Interactive_Journey_V15.html:
     Q1 CCTV feed available?   Yes → Q2   ·  No → Assign to Operator
     Q2 Swap successful?       Yes → Close Ticket  ·  No → Q3
     Q3 Reason swap did not succeed (dropdown) → Assign to Operator
     Q4 Review notes (text) — always shown
   ────────────────────────────────────────────────────────────── */
import { yn } from './stage2Rules'

const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const MATCHERS = {
  cctv:   'cctv',
  swap:   'swap successful',
  reason: 'reason swap did not succeed',
  notes:  'review notes',
}

/** get-checklist questions → { cctv, swap, reason, notes } (null if not the L3 CNC-review checklist) */
export function identify(questions) {
  const map = {}
  Object.entries(MATCHERS).forEach(([k, m]) => { map[k] = questions.find(q => norm(q.text).includes(m)) || null })
  return map.cctv && map.swap ? map : null
}

export function visible(map, answers) {
  const out = [map.cctv]
  if (yn(map.cctv, answers) === 'yes') {
    out.push(map.swap)
    if (yn(map.swap, answers) === 'no' && map.reason) out.push(map.reason)
  }
  if (map.notes) out.push(map.notes)
  return out
}

export function hints(map, answers) {
  const h = {}
  if (yn(map.cctv, answers) === 'no') h[map.cctv.id] = { text: '→ Assign to Operator', tone: 'bad' }
  const swap = yn(map.swap, answers)
  if (swap === 'yes') h[map.swap.id] = { text: '→ Close Ticket', tone: 'ok' }
  if (swap === 'no') h[map.swap.id] = { text: '→ Assign to Operator', tone: 'bad' }
  if (map.reason && answers[map.reason.id]) h[map.reason.id] = { text: '→ Assign to Operator', tone: 'bad' }
  return h
}

/** design note shown under the CCTV question (as in the HTML) */
export function notes(map) {
  return {
    [map.cctv.id]: {
      label: 'Open contradiction:',
      text: 'the SOP is a no-CCTV process yet Stage III depends on footage. Station coverage must be confirmed or this branch always falls to Operator.',
    },
  }
}

export function pills(map, answers) {
  return [['cctv', 'CCTV feed'], ['swap', 'Swap successful']]
    .map(([k, label]) => ({ key: k, label, v: yn(map[k], answers) }))
    .filter(p => p.v)
    .map(p => ({ key: p.key, label: p.label, ok: p.v === 'yes' }))
}

export function submitOutcome(map, answers) {
  if (yn(map.cctv, answers) === 'no') return {
    tone: 'danger', title: 'No CCTV feed available', subtitle: 'Cannot validate remotely.',
    body: [{ text: 'No footage for this station and window, so the case cannot be validated from CNC. Ticket goes to the Operator.' }],
    actions: ['OPERATOR'],
  }
  if (yn(map.swap, answers) === 'no') {
    const reason = map.reason?.options.find(o => o.id === String(answers[map.reason.id] ?? ''))
    return {
      tone: 'danger', title: 'Swap did not succeed at Stage III', subtitle: 'Physical intervention required.',
      body: [
        { label: 'Reason:', text: reason?.label || answers[map.reason?.id] || '—' },
        { text: 'CNC could not complete the swap remotely, so the ticket goes to the Operator.' },
      ],
      actions: ['OPERATOR'],
    }
  }
  return {
    tone: 'success', title: 'Swap successful at Stage III', subtitle: 'CNC completed the swap.',
    body: [{ text: 'CNC reviewed the footage and completed the swap. The ticket closes here.' }],
    actions: ['CLOSE'],
  }
}

/** "Rider unreachable" (Stage 3 call drop) → always Operator */
export function dropOutcome() {
  return {
    dest: 'OPERATOR', subtitle: 'Stage 3 — rider unreachable on callback',
    body: 'CNC could not reach the rider. Ticket goes to the Operator.',
  }
}

/** context strip above the Stage 3 form, built from the submitted L2 answers */
export function contextLine(l2Rows = []) {
  const find = (m) => l2Rows.find(r => norm(r.questionText).includes(m))
  const swap = find('swap successful')
  const label = (r) => r?.optionLabel || r?.description || ''
  if (swap && norm(label(swap)) === 'yes') return { html: ['Swap succeeded but ', 'battery handover was not confirmed'] }
  const reason = find('reason swap did not succeed')
  if (swap || reason) return { html: ['Swap did not succeed — reason: ', (label(reason).split('→')[0].trim() || '—')] }
  return null
}

export const dropButtonLabel = '📞 Rider unreachable'
export const summaryTitle = 'Stage 3 Summary'
